import { HttpErrorResponse, HttpHeaders, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { BillingGateService, type BillingGateReason } from '../billing-gate';
import { TenantService } from '../tenant/tenant.service';
import { AuthService } from '../services/auth.service';

/**
 * Paths que devem passar mesmo com o billing gate ativo. Usa substring match
 * (não URL completa) para cobrir variações de base URL entre envs.
 *
 * - `/api/auth/` e `/auth/`: login, signin, signup, refresh, logout, forgot-password, accept-invite.
 * - `/api/billing/`: toda a superfície de billing (current-subscription, usage, invoices,
 *   subscriptions, subscriptions/upgrade|downgrade|cancel|reactivate).
 * - `/api/companies`: leitura do endereço da empresa para montar `billing_address` da
 *   tokenização Pagar.me. Sem isso o checkout de cartão não consegue reativar a assinatura
 *   de uma empresa cancelada (catch-22: precisa do endereço pra pagar, mas o gate suprime).
 *   Backend continua aplicando suas próprias políticas — escrever ainda fica bloqueado lá.
 * - `assets/`: arquivos estáticos (i18n, ícones, imagens) — a própria tela de faturamento
 *   depende deles; suprimir quebra as traduções após F5 com gate ativo.
 */
const ALLOWLIST_PATH_FRAGMENTS = [
  '/api/auth/',
  '/auth/',
  '/api/billing/',
  '/api/companies',
  'assets/',
];

function isAllowlistedRequest(url: string): boolean {
  const normalized = url.toLowerCase();
  return ALLOWLIST_PATH_FRAGMENTS.some((fragment) => normalized.includes(fragment));
}

function extractBillingGateReason(error: HttpErrorResponse): BillingGateReason | null {
  const body = (error.error ?? {}) as { errorKey?: unknown; message?: unknown; error?: unknown };
  const code =
    typeof body.errorKey === 'string' && body.errorKey.trim()
      ? body.errorKey.trim()
      : typeof body.error === 'string' && body.error.trim()
        ? body.error.trim()
        : undefined;
  const message =
    typeof body.message === 'string' && body.message.trim() ? body.message.trim() : undefined;
  if (!code && !message) return null;
  return { code, message };
}

function readCompanyKeyFromRouterUrl(url: string): string | null {
  const match = url.match(/\/c\/([^/?#]+)/i);
  return match?.[1] ? decodeURIComponent(match[1]).trim() || null : null;
}

/**
 * Captura 402 Payment Required em qualquer resposta HTTP:
 *  - ativa o `BillingGateService` para a empresa atual;
 *  - redireciona para `/c/:companyKey/empresa/faturamento` (preservando a chave da rota);
 *  - suprime requisições subsequentes que não estejam na allowlist enquanto o gate estiver ativo,
 *    para evitar cascata de 402 (dashboard + widgets disparando dezenas de chamadas).
 *
 * Endpoints de auth e billing ficam livres para que o fluxo de pagamento continue utilizável.
 * A limpeza do gate acontece no `BillingStore` quando a assinatura volta a `Active`.
 */
export const paymentRequiredInterceptor: HttpInterceptorFn = (req, next) => {
  const gate = inject(BillingGateService);
  const router = inject(Router);
  const tenant = inject(TenantService);
  const auth = inject(AuthService);

  if (gate.blocked() && !isAllowlistedRequest(req.url)) {
    // Suprime cascata: não chamamos `next()` — devolvemos um erro sintético 402
    // para que quem estiver esperando trate como "não foi possível" sem bater no backend.
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 402,
          statusText: 'Payment Required',
          url: req.url,
          headers: new HttpHeaders(),
          error: { errorKey: 'Billing_Blocked_Client', message: 'billing.gate.requestSuppressed' },
        })
    );
  }

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 402) {
        return throwError(() => error);
      }

      const companyId =
        tenant.getActiveCompanyId() ?? auth.currentUser()?.companyId?.trim() ?? null;
      if (companyId) {
        gate.block(companyId, extractBillingGateReason(error));
      }

      const companyKey =
        tenant.companyKey() ??
        auth.currentUser()?.companyKey?.trim() ??
        readCompanyKeyFromRouterUrl(router.url) ??
        null;

      if (companyKey) {
        const alreadyOnBilling = router.url
          .toLowerCase()
          .includes(`/c/${companyKey.toLowerCase()}/empresa/faturamento`);
        if (!alreadyOnBilling) {
          void router.navigate(['/c', companyKey, 'empresa', 'faturamento']);
        }
      }

      return throwError(() => error);
    })
  );
};
